import React, { useEffect, useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { assetService, cacheService } from "../../../Core/Api/services";
import { UpdateMetadataFieldsRequest } from "../../../Core/Api/Types/update-metadata-fields-request";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import { useSelector } from "react-redux";
import { IDbBaseMetadataValueInfos } from "../../../Core/Api/Types/i-db-base-metadata-value-infos";
import { GetCulturesList } from "../../../Redux/Reducers/redux-slice-bundle-maker";
import {
  generateArtworkCultureIdsMetadata,
  generateLanguagePictureMetadata,
  generateOrientationMetation,
} from "../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { useTranslation } from "react-i18next";

export type onCloseEditArtworkProps = {
  artworkId: number;
  metadata: IDbBaseMetadataValueInfos[];
};

export type EditArtworkMetadataProps = {
  data: any;
  onClose: (props: onCloseEditArtworkProps) => void;
};

export type ModalEditArtworkMetadataProps = CommonModalProps<EditArtworkMetadataProps>;

const ModalEditArtworkMetadata = React.forwardRef<any, ModalEditArtworkMetadataProps>((props, ref) => {
  const { t } = useTranslation();
  const { data, onClose, closeModal } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();

  //------local state/select store values------

  const cultures = useSelector(GetCulturesList);
  const [selectedCultures, setSelectedCultures] = useState<DbCultureInfos[]>([]);
  const [selectedLanguageId, setSelectedLanguage] = useState<number>(-1);
  const [orientation, setOrientation] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);

  // Compute select language options
  const languages = useMemo(() => {
    const result: PlatformLanguageCode[] = [];
    cultures &&
      cultures.length > 0 &&
      cultures.forEach((c) => {
        c.languagesMap &&
          c.languagesMap.length > 0 &&
          c.languagesMap.forEach((l) => {
            !result.find((x) => x.id === l.id) && result.push(l);
          });
      });
    return result;
  }, [cultures]);

  // init preselected cultures and language
  useEffect(() => {
    const artworkCultureIdsMetadataValue = data.metadata.find(
      (x) => cacheService.GetMetadata().find((m) => m.tagName === "artworkCultureIds")?.id === x.id
    )?.value;
    if (artworkCultureIdsMetadataValue && artworkCultureIdsMetadataValue.length > 0) {
      setSelectedCultures(artworkCultureIdsMetadataValue.map((x) => cultures.find((c) => c.id === x)));
    }

    const languagePictureMetadataValue = data.metadata.find(
      (x) =>
        cacheService.GetMetadata().find((m) => m.tagName === "languagePicture" && m.layer === ObjectLayer.Asset)?.id ===
        x.id
    )?.value;
    if (languagePictureMetadataValue && languagePictureMetadataValue.id) {
      setSelectedLanguage(languagePictureMetadataValue.id);
    }

    const orientationMetadata = data.metadata.find(
      (x) => cacheService.GetMetadata().find((m) => m.tagName === "contentSubType")?.id === x.id
    )?.value;
    if (orientationMetadata) {
      setOrientation(orientationMetadata);
    }
  }, [data]);

  const handleSave = () => {
    setIsSaving(true);
    const artworkCultureIds = generateArtworkCultureIdsMetadata(selectedCultures.map((x) => x.id));
    const languagePicture = generateLanguagePictureMetadata(languages.find((l) => l.id === selectedLanguageId));
    const orientationValue = generateOrientationMetation(orientation);
    const metadata: IDbBaseMetadataValueInfos[] = [];

    if (orientationValue) metadata.push(orientationValue);
    if (artworkCultureIds && artworkCultureIds.value && artworkCultureIds.value.length > 0)
      metadata.push(artworkCultureIds);

    if (languagePicture && languagePicture.value) metadata.push(languagePicture);

    assetService
      .UpdateMetadataFields(
        new UpdateMetadataFieldsRequest({
          objectId: data.id,
          metadata: metadata,
        })
      )
      .then((res) => {
        setIsSaving(false);
        if (res.success) {
          onClose({ artworkId: data.id, metadata: metadata });
          closeModal();
        } else {
          enqueueCustomSnackbar(res.message || t("alert:error.updatingArtwork"), "error");
        }
      });
  };

  return (
    <Modal
      width={750}
      closeModal={closeModal}
      title={t("modal:title.artworkMetadataEdition")}
      subheader={
        <Typography margin={2} variant="subtitle2" fontWeight={600}>
          {data.name}
        </Typography>
      }
      footer={
        <Stack position="relative" direction="row" spacing={2}>
          <Button onClick={() => closeModal()} color="inherit">
            {t("common:cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave()}
            startIcon={<AddIcon />}
            sx={{ flex: 1 }}
            disabled={isSaving}
          >
            {t("common:save")}
          </Button>
          {isSaving && (
            <CircularProgress
              size={24}
              sx={{
                position: "absolute",
                top: "8px",
                right: "12px",
              }}
            />
          )}
        </Stack>
      }
    >
      {cultures && cultures.length > 0 && (
        <>
          <InputLabel htmlFor="select-cultures">
            <Typography variant="subtitle2" fontWeight={600}>
              {t("field:label.associatedCounties")}
            </Typography>
          </InputLabel>
          <Autocomplete
            sx={{ marginTop: "20px" }}
            value={selectedCultures}
            onChange={(e, newValue) => setSelectedCultures(newValue)}
            multiple
            id="select-cultures"
            options={[...cultures].sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))}
            getOptionLabel={(option) => option.englishName || ""}
            defaultValue={[]}
            filterSelectedOptions
            fullWidth
            renderInput={(params) => (
              <OutlinedInput
                rows={1}
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
                placeholder={t("field:placeholder.selectCountries")}
              />
            )}
          />
        </>
      )}
      <Divider sx={{ marginTop: "24px", marginBottom: "24px" }} />
      {languages && languages.length && (
        <>
          <InputLabel htmlFor="select-language">
            <Typography variant="subtitle2" fontWeight={600}>
              {t("common:language")}
            </Typography>
          </InputLabel>
          <Select
            onChange={(e) => setSelectedLanguage(e.target.value as number)}
            value={selectedLanguageId}
            size="small"
            id="select-language"
            sx={{ width: "100%", marginTop: "20px" }}
          >
            {languages.map((l) => (
              <MenuItem value={l.id || undefined}>{l.label}</MenuItem>
            ))}
          </Select>
        </>
      )}
      <Divider sx={{ marginTop: "24px", marginBottom: "24px" }} />
      <>
        <InputLabel htmlFor="select-orientation">
          <Typography variant="subtitle2" fontWeight={600}>
            {t("field:label.orientation")}
          </Typography>
        </InputLabel>
        <Select
          onChange={(e) => setOrientation(e.target.value)}
          value={orientation}
          size="small"
          id="select-orientation"
          sx={{ width: "100%", marginTop: "20px" }}
        >
          <MenuItem value={"ArtworkPosterHorizontal"}>{t("common:horizontal")}</MenuItem>
          <MenuItem value={"ArtworkPosterVertical"}>{t("common:vertical")}</MenuItem>
        </Select>
      </>
    </Modal>
  );
});

export default ModalEditArtworkMetadata;
